import type { InvoiceProduct } from './products-table';

/**
 * Pure client-side validation for the invoice/quote generator, extracted from
 * `generate-invoice-form.tsx`. No money is rendered here (it only inspects the
 * already-computed `totalAmount` number), so it's wall-neutral; it also lives
 * under the financial-only-allowlisted `generate-invoice-form/` tree.
 */
export interface InvoiceValidationErrors {
  customer?: string;
  products?: {
    [key: string]: {
      partNumber?: string;
      description?: string;
      seats?: string;
      chargePerSeat?: string;
    };
  };
  general?: string;
  dueDate?: string;
}

/**
 * Validate the form before submit. A product row is only validated once it has
 * ANY data (so an untouched blank row doesn't error); a row with data must have
 * a description and a positive quantity. At least one such row is required.
 *
 * A document may total $0.00 (CEO, 2026-09-18). A unit price of zero is a
 * finished line in every pricing mode — a fully discounted order, a no-charge
 * warranty replacement, an "included" catalogue entry on its own, a "% of
 * order" line whose base happens to be nothing — so neither a zero unit price
 * nor a zero grand total is refused here any more. crm-api agrees: its
 * `total_amount` rule is `>= 0` for every document. Quantity stays positive
 * (a line of nothing is a typo, not a price) and a description is still
 * required, because those say WHAT is on the document rather than what it
 * costs.
 *
 * Note this is about the DOCUMENT, not the price BOOK: an "included" feature
 * and an UNPRICED one are still different things in CRM ▸ Pricing, and the
 * catalogue picker still labels an unpriced pick "No price set" rather than
 * "$0.00" (`products-table.tsx`). What changed is only that the operator may
 * now leave such a line at zero and issue the document.
 */
export function validateInvoiceForm(input: {
  hasContact: boolean;
  invoiceNumber: string;
  hasDeal: boolean;
  products: InvoiceProduct[];
  totalAmount: number;
  /** The document is addressed to a RESELLER, not a customer (2026-09-13).
   * XOP's business with a reseller has no customer and no agreement behind it,
   * so the customer/deal checks below do not apply — but the number and the
   * product rules still do. */
  billsAReseller?: boolean;
}): InvoiceValidationErrors {
  const errors: InvoiceValidationErrors = {};

  if (input.billsAReseller) {
    if (!input.invoiceNumber.trim()) {
      errors.customer =
        'Invoice number is missing. Please wait for auto-generation or enter manually.';
    }
  // Customer + deal — be specific about what's missing.
  } else if (!input.hasContact) {
    errors.customer =
      'Please select a customer or reseller. The document number is generated once one is picked.';
  } else if (!input.invoiceNumber.trim()) {
    errors.customer = 'Invoice number is missing. Please wait for auto-generation or enter manually.';
  } else if (!input.hasDeal) {
    errors.customer = 'Customer agreement is still loading. Please wait a moment and try again.';
  }

  // Products.
  const productErrors: NonNullable<InvoiceValidationErrors['products']> = {};
  let hasValidProduct = false;

  input.products.forEach((product) => {
    const prodErrors: NonNullable<InvoiceValidationErrors['products']>[string] = {};

    // Only validate a row once it carries any data (an untouched blank row is fine).
    const hasAnyData =
      product.partNumber.trim() ||
      product.description.trim() ||
      product.seats > 0 ||
      product.chargePerSeat > 0;

    if (hasAnyData) {
      if (!product.description.trim()) prodErrors.description = 'Description is required';
      if (product.seats <= 0) prodErrors.seats = 'Quantity must be greater than 0';
      // A unit price of 0 is allowed in EVERY pricing mode since 2026-09-18.
      // It used to be refused unless the line was `included`, which is what
      // made a $0.00 document impossible to raise; the three-way split that
      // lived here (included fine / percent "add priced lines" / everything
      // else refused) existed only to force a non-zero figure onto the
      // document, and the CEO reversed that. A negative price is still
      // impossible — the quantity/price inputs clamp at 0 — and crm-api
      // refuses a negative line amount independently.
      //
      // `hasValidProduct` deliberately no longer looks at the price: a row
      // that says WHAT it is and HOW MANY is a complete line whatever it
      // costs.
      if (product.description.trim() && product.seats > 0) {
        hasValidProduct = true;
      }
    }

    if (Object.keys(prodErrors).length > 0) {
      productErrors[product.id] = prodErrors;
    }
  });

  if (!hasValidProduct) {
    errors.general = 'Please add at least one product with a description and a quantity.';
  }
  if (Object.keys(productErrors).length > 0) {
    errors.products = productErrors;
  }
  // The derived grand total is deliberately NOT checked. $0.00 is a document
  // somebody meant to raise; crm-api's `total_amount >= 0` is the backstop.

  return errors;
}
